"use client";

import { CtaComponentProps } from "@/cms/models";
import {
  getBorderRadius,
  getDesktop,
  getDesktopSize,
  getMargins,
  getMobile,
  getMobileSize,
  getPaddings,
} from "@/utils/theme-helper";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

type Ripple = {
  id: number;
  x: number;
  y: number;
  size: number;
};

export default function RippleCtaButton(props: {
  children?: React.ReactNode;
  className?: string;
  config?: Partial<CtaComponentProps>;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  type?: "button" | "submit" | "reset" | undefined;
  isActive?: boolean;
}) {
  let { config } = props;

  config = {
    ...{
      link: "#",
      label: "",

      rounded: "none",
      margin: "0",
      justify: "center",
      textColor: "var(--button-text-color)",
      hoverTextColor: "var(--button-hover-text-color)",
      activeTextColor: "var(--button-active-text-color)",
      bgColor: "var(--button-bg-color)",
      hoverBgColor: "var(--button-hover-bg-color)",
      activeBgColor: "var(--button-active-bg-color)",
      borderColor: "var(--button-border-color)",
      hoverBorderColor: "var(--button-hover-border-color)",
      activeBorderColor: "var(--button-active-border-color)",
      size: "md",
      padding: "sm",
      width: "auto",
      height: "auto",
      disableAnimation: false,
    },
    ...(config ?? {}),
  };

  const [ripples, setRipples] = useState<Ripple[]>([]);
  const idRef = useRef(0);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const createRipple = useCallback((originX: number, originY: number) => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const localX = originX - rect.left;
    const localY = originY - rect.top;
    const dx = Math.max(localX, rect.width - localX);
    const dy = Math.max(localY, rect.height - localY);
    const radius = Math.sqrt(dx * dx + dy * dy);
    const size = radius * 2;

    const id = ++idRef.current;
    setRipples((prev) => [...prev, { id, x: localX, y: localY, size }]);

    return id;
  }, []);

  const removeLastRipple = useCallback(() => {
    setRipples((prev) => (prev.length ? prev.slice(0, prev.length - 1) : prev));
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if ((event as any).isPrimary) {
        createRipple(event.clientX, event.clientY);
      }
    },
    [createRipple]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.repeat) return;
      if (event.key === " " || event.key === "Enter") {
        const button = buttonRef.current;
        if (!button) return;
        const rect = button.getBoundingClientRect();
        createRipple(rect.left + rect.width / 2, rect.top + rect.height / 2);
      }
    },
    [createRipple]
  );

  const onKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === " " || event.key === "Enter") {
        removeLastRipple();
      }
    },
    [removeLastRipple]
  );

  const mobileWidth = getMobile("none", config.width);
  const desktopWidth = getDesktop("none", config.width);
  const mobileHeight = getMobile("none", config.height);
  const desktopHeight = getDesktop("none", config.height);

  return (
    <div
      data-type="CtaComponent"
      className={twMerge(
        `flex md:homepage-text-${getDesktopSize(config?.size)} homepage-text-${getMobileSize(config?.size)} ${getMargins(config.margin)} justify-${config.justify} ${mobileWidth !== "none" ? "w-[var(--mobile-width)]" : ""}`,
        `${desktopWidth !== "none" ? "md:w-[var(--desktop-width)]" : ""} ${mobileHeight !== "none" ? "h-[var(--mobile-height)]" : ""} ${desktopHeight !== "none" ? "md:h-[var(--desktop-height)]" : ""}`
      )}
      style={
        {
          "--mobile-width": mobileWidth,
          "--desktop-width": desktopWidth,
          "--mobile-height": mobileHeight,
          "--desktop-height": desktopHeight,
        } as React.CSSProperties
      }
    >
      <motion.button
        ref={buttonRef}
        disabled={props.disabled}
        type={props.type || "button"}
        onClick={(e) => props.onClick?.(e)}
        style={
          {
            "--rounding-mobile": getBorderRadius(getMobile("", config.rounded)),
            "--rounding-desktop": getBorderRadius(
              getDesktop("", config.rounded)
            ),
          } as React.CSSProperties
        }
        className={twMerge(
          `relative homepage-rounded border ${config.padding ? getPaddings(config.padding) : "px-3 py-2"} flex justify-center lg:min-w-[122px] disabled:opacity-20`,
          props.className
        )}
        onPointerDown={onPointerDown}
        onPointerUp={removeLastRipple}
        onPointerCancel={removeLastRipple}
        onPointerLeave={removeLastRipple}
        onBlur={removeLastRipple}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        animate={props.isActive ? "active" : undefined}
        whileHover={{
          borderColor: config.hoverBorderColor,
          backgroundColor: config.hoverBgColor,
          color: config.hoverTextColor,
        }}
        initial={{
          borderColor: config.borderColor,
          backgroundColor: config.bgColor,
          color: config.textColor,
        }}
        variants={{
          active: {
            borderColor: config.activeBorderColor,
            backgroundColor: config.activeBgColor,
            color: config.activeTextColor,
          },
        }}
        transition={{
          duration: 0.2,
          ease: "linear",
        }}
      >
        {props.children}
        <span className="ripple-container" aria-hidden>
          <AnimatePresence>
            {ripples.map((ripple) => (
              <motion.span
                key={ripple.id}
                className="ripple"
                style={{
                  width: ripple.size,
                  height: ripple.size,
                  left: ripple.x - ripple.size / 2,
                  top: ripple.y - ripple.size / 2,
                }}
                initial={{ opacity: 0, transform: "scale(0)" }}
                animate={{
                  opacity: 0.4,
                  transform: "scale(1)",
                  transition: { duration: 0.3 },
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.55,
                  ease: "easeOut",
                }}
              />
            ))}
          </AnimatePresence>
        </span>
      </motion.button>
      <Stylesheet />
    </div>
  );
}

function Stylesheet() {
  return (
    <style>{`
      .ripple-container {
        position: absolute;
        inset: 0;
        overflow: hidden;
        border-radius: inherit;
        pointer-events: none;
      }

      .ripple {
        position: absolute;
        border-radius: 50%;
        background-color: currentColor;
        will-change: opacity, transform;
      }
    `}</style>
  );
}
